import {useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';
//formik
import {Formik} from 'formik';
//mui
import {Box, useMediaQuery} from '@mui/material';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getEvents, getSponsorCategoriesSelector} from '@/store/organization-event';
//context
//service
import {OrganizationEventService} from '@/utils/services/organization-event.service';
//helpers
import {getErrorMessage} from '@/utils/helpers';
import {sendPresignedImage} from '@/pages/Organization/Profile/General/ChangePhotoModal';
import {useGetOrganizationId} from '@/utils/hooks';
//types
import {IOrganizationEventRequest, IOrganizationSponsorCategoryResponse} from '@/utils/types';
//constants
import {DONE, FILLED} from '@/utils/constants';
//components
import Stepper from '@/shared/components/Stepper';
import Modal from '@/shared/components/Modal';
//icons
import {ReactComponent as CheckIcon} from '@/assets/icons/rudiobutton-checked.svg';
import {EVENT_IMAGE, ROUTER_URL} from '@/utils/enums';
import {createNewEventStyle} from './CreateNewEvent.styles';
import {
  CREATE_EVENT_STEP,
  createEventSteps,
  getNewEventValues,
} from '@/components/Organization/Dashboard/CreateNewEvent/helpers';
import {
  CreateEventFormValues,
  initialValues,
  validationSchema,
} from '@/components/Organization/Dashboard/CreateNewEvent/formConfiguration';
import {
  CreateNewEventContext,
  CreateNewEventContextType,
} from '@/components/Organization/Dashboard/CreateNewEvent/OrganizationSignUpContext';
import EventSetting from '@/components/Organization/Dashboard/CreateNewEvent/EventSetting';
import TableAndTickets from '@/components/Organization/Dashboard/CreateNewEvent/TableAndTickets';
import EventWebpage from '@/components/Organization/Dashboard/CreateNewEvent/EventWebpage';
import {useNavigate} from 'react-router-dom';
import {resolveCategoryId} from '@/components/Organization/Dashboard/CreateNewEvent/EventSetting/EventSponsors/AddSponsorModalForm/helpers';

export const getEventImage = async (logo: File, type: EVENT_IMAGE) => {
  const presignedLogoUrl = await OrganizationEventService.presignedImage(type);

  await sendPresignedImage(presignedLogoUrl.data.url, logo);

  return presignedLogoUrl.data.key.split('/').pop();
};

const AddEventPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLaptop = useMediaQuery('(min-width: 1200px)');

  const [activeStep, setActiveStep] = useState(0);

  const organizationId = useGetOrganizationId();
  const sponsorCategories = useAppSelector(getSponsorCategoriesSelector);

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const onOpenSuccessModal = () => setIsSuccessModalOpen(!isSuccessModalOpen);

  const handleNextStep = () => setActiveStep(activeStep + 1);
  const handleBackStep = () => setActiveStep(activeStep - 1);

  const formData = useMemo(() => {
    switch (activeStep) {
      case CREATE_EVENT_STEP.SETTING:
        return <EventSetting />;

      case CREATE_EVENT_STEP.TABLES_AND_TICKETS:
        return <TableAndTickets />;

      case CREATE_EVENT_STEP.WEBPAGE:
        return <EventWebpage />;

      default:
        return <EventWebpage />;
    }
  }, [activeStep]);

  const submitClickHandler = async (values: CreateEventFormValues) => {
    if (!organizationId) return;

    if (activeStep === CREATE_EVENT_STEP.WEBPAGE) {
      try {
        const image = await getEventImage(values.logo as File, EVENT_IMAGE.EVENT);
        const raffleImage = values.prizeLogo ? await getEventImage(values.prizeLogo as File, EVENT_IMAGE.RAFFLE) : null;

        const newEventValues: IOrganizationEventRequest = getNewEventValues(values, raffleImage);

        const {data} = await OrganizationEventService.createEvent({
          ...newEventValues,
          image,
          sponsors: [],
        });

        if (data) {
          const existingCategories = sponsorCategories || [];
          const categoriesCache = new Map<string, number>();

          const uniqueCategoryNames = [
            ...new Set(
              values.sponsors.map((s) => s?.category?.name?.trim()).filter((name): name is string => Boolean(name))
            ),
          ];
          for (const categoryName of uniqueCategoryNames) {
            // eslint-disable-next-line no-await-in-loop
            await resolveCategoryId(organizationId, categoryName, existingCategories, categoriesCache);
          }

          for (const sponsor of values.sponsors) {
            // eslint-disable-next-line no-await-in-loop
            const categoryId = await resolveCategoryId(
              organizationId,
              sponsor?.category?.name || '',
              existingCategories,
              categoriesCache
            );

            // eslint-disable-next-line no-await-in-loop
            const presignedUrl = await OrganizationEventService.presignedImage(EVENT_IMAGE.SPONSOR);
            // eslint-disable-next-line no-await-in-loop
            await sendPresignedImage(presignedUrl.data.url, sponsor.logo);

            const sponsorsImageData = {
              name: sponsor.name,
              logo: presignedUrl.data.key.split('/').pop() as string,
              categoryId,
              sortOrder: sponsor.sortOrder,
            };

            // eslint-disable-next-line no-await-in-loop
            await OrganizationEventService.addSponsorToEvent(data, sponsorsImageData);
          }
        }

        await dispatch(getEvents(organizationId)).unwrap();
        toast.success('Event created successfully');
        navigate(ROUTER_URL.ORGANIZATION_DASHBOARD);
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    } else {
      handleNextStep();
    }
  };

  useEffect(() => {
    if (isLaptop) {
      navigate(ROUTER_URL.ORGANIZATION_DASHBOARD);
    }
  }, [isLaptop]);

  const CreateNewEventContextValue: CreateNewEventContextType = useMemo(() => {
    return {
      activeStep,
      handleNextStep,
      handleBackStep,
    };
  }, [activeStep, handleNextStep, handleBackStep]);

  return (
    <CreateNewEventContext.Provider value={CreateNewEventContextValue}>
      <Box sx={createNewEventStyle.wrapper}>
        <Stepper steps={createEventSteps} activeStep={activeStep} sx={createNewEventStyle.stepper} />

        <Formik
          initialValues={initialValues}
          onSubmit={submitClickHandler}
          validationSchema={validationSchema(activeStep)}
          validateOnChange={false}
        >
          {formData}
        </Formik>

        <Modal
          title="Event Created"
          description="Your event has been successfully created."
          mainIcon={<CheckIcon />}
          closeButtonTitle={DONE}
          onClose={onOpenSuccessModal}
          open={isSuccessModalOpen}
          isShowCloseButton
          closeButtonVariant={FILLED}
          sx={createNewEventStyle.modal}
        />
      </Box>
    </CreateNewEventContext.Provider>
  );
};

export default AddEventPage;
